import { detectBrand, type SiteBrand } from './branding';

let activeBrand: SiteBrand | null = null;
let faviconRequestId = 0;

/**
 * Update the document title based on the current brand
 */
export const updateDocumentTitle = (brandOverride?: SiteBrand): void => {
  const brand = brandOverride ?? detectBrand();
  const siteName = brand === 'mcv' ? 'MedCredVault' : 'HealthcareLX';
  document.title = siteName;
};

/**
 * Update favicon based on the current brand
 * This function expects favicons to be located at:
 * - /favicon-mcv.ico for MedCredVault
 * - /favicon-hlx.ico for HealthcareLX
 * Falls back to default /favicon.ico if brand-specific files don't exist
 */
export const updateFavicon = (brandOverride?: SiteBrand): void => {
  const brand = brandOverride ?? detectBrand();
  const faviconPath = `/favicon-${brand}.ico`;

  // Check if a favicon link element already exists
  let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;

  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }

  // Track request to avoid race conditions from overlapping Image loads
  const currentRequestId = ++faviconRequestId;
  const testImg = new Image();
  testImg.src = faviconPath;

  testImg.onerror = () => {
    if (currentRequestId !== faviconRequestId) return;
    link.href = '/favicon.ico';
  };

  testImg.onload = () => {
    if (currentRequestId !== faviconRequestId) return;
    link.href = faviconPath;
  };
};

/**
 * Update meta tags based on the current brand
 */
export const updateMetaTags = (brandOverride?: SiteBrand): void => {
  const brand = brandOverride ?? detectBrand();
  const siteName = brand === 'mcv' ? 'MedCredVault' : 'HealthcareLX';
  const description =
    brand === 'mcv'
      ? 'MedCredVault - Secure credential management for healthcare providers'
      : 'HealthcareLX - Find your next healthcare opportunity';

  // Update or create meta description
  let metaDesc = document.querySelector("meta[name='description']") as HTMLMetaElement;
  if (!metaDesc) {
    metaDesc = document.createElement('meta');
    metaDesc.name = 'description';
    document.head.appendChild(metaDesc);
  }
  metaDesc.content = description;

  // Update or create og:site_name
  let ogSite = document.querySelector("meta[property='og:site_name']") as HTMLMetaElement;
  if (!ogSite) {
    ogSite = document.createElement('meta');
    ogSite.setAttribute('property', 'og:site_name');
    document.head.appendChild(ogSite);
  }
  ogSite.content = siteName;

  // Update or create og:title
  let ogTitle = document.querySelector("meta[property='og:title']") as HTMLMetaElement;
  if (!ogTitle) {
    ogTitle = document.createElement('meta');
    ogTitle.setAttribute('property', 'og:title');
    document.head.appendChild(ogTitle);
  }
  ogTitle.content = siteName;
};

/**
 * Initialize all brand-specific runtime configurations
 * This should be called once on app initialization
 */
export const initializeBranding = (brandOverride?: SiteBrand): void => {
  const brand = brandOverride ?? detectBrand();
  if (brand === activeBrand) return;
  activeBrand = brand;
  updateDocumentTitle(brand);
  updateFavicon(brand);
  updateMetaTags(brand);
};

/**
 * Get the current brand as a string
 * Useful for logging and debugging
 */
export const getCurrentBrand = (): SiteBrand => detectBrand();

/**
 * Get the appropriate API URL for the current brand
 * In production, this would return different URLs if needed
 */
export const getApiUrl = (): string => {
  return import.meta.env.VITE_API_BASE_URL || '/api';
};

/** Reset module state — for use in tests only. */
export const _resetActiveBrand = (): void => {
  activeBrand = null;
  faviconRequestId = 0;
};
